import { defineStore } from 'pinia'
import { ref } from 'vue'
import messageService from '@services/MessageService'
import { MessageTypes, QueryTypes } from '@types'
import { message } from 'antdv-next'

/**
 * 消息 Store
 */
export const useMessageStore = defineStore('message', () => {
  const messages = ref<MessageTypes.Message[]>([])
  const total = ref(0)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const loading = ref(false)
  const unreadCount = ref(0)
  const filters = ref({
    search: '',
    type: '',
    is_read: undefined as boolean | undefined,
    dateRange: null as [string, string] | null
  })

  /**
   * 获取消息列表
   */
  const fetchMessages = async (params: Partial<QueryTypes.ListRequestParams & { is_read?: boolean }> = {}) => {
    loading.value = true
    try {
      const apiParams: QueryTypes.ListRequestParams & { is_read?: boolean; type?: string } = {
        page: params.page || currentPage.value,
        per_page: params.per_page || pageSize.value,
        search: params.search || filters.value.search,
        is_read: params.is_read !== undefined ? params.is_read : filters.value.is_read,
        type: (params as Record<string, unknown>).type as string || filters.value.type
      }

      if (filters.value.dateRange) {
        apiParams.start_date = filters.value.dateRange[0]
        apiParams.end_date = filters.value.dateRange[1]
      }
      
      const response = await messageService.getMessages(apiParams)
      if (response.success && response.data) {
        messages.value = response.data
        if (response.meta) {
          total.value = response.meta.total
          currentPage.value = response.meta.page
          pageSize.value = response.meta.per_page
        }
      }
    } catch (error) {
      console.error('获取消息列表失败:', error)
    } finally {
      loading.value = false
    }
  }

  /**
   * 更新未读消息数量
   */
  const updateUnreadCount = async () => {
    try {
      const response = await messageService.getMessages({ is_read: false, page: 1, per_page: 1 })
      if (response.success && response.meta) {
        unreadCount.value = response.meta.total
      }
    } catch (error) {
      console.error('获取未读消息数量失败:', error)
    }
  }

  /**
   * 标记消息为已读
   */
  const markAsRead = async (messageId: number) => {
    try {
      const response = await messageService.markAsRead(messageId)
      if (response.success) {
        // 更新当前消息列表中的消息状态
        const index = messages.value.findIndex(msg => msg.id === messageId)
        if (index !== -1) {
          messages.value[index].is_read = true
        }
        // 减少未读数量
        if (unreadCount.value > 0) {
          unreadCount.value--
        }
      }
    } catch (error) {
      console.error('标记消息已读失败:', error)
    }
  }

  /**
   * 批量标记消息为已读
   */
  const markMultipleAsRead = async (messageIds: number[]) => {
    try {
      const response = await messageService.markMultipleAsRead(messageIds)
      if (response.success) {
        // 更新当前消息列表中的消息状态
        let count = 0;
        messageIds.forEach(id => {
          const index = messages.value.findIndex(msg => msg.id === id)
          if (index !== -1 && !messages.value[index].is_read) {
            messages.value[index].is_read = true
            count++;
          }
        })
        await updateUnreadCount()
      }
    } catch (error) {
      console.error('批量标记消息已读失败:', error)
    }
  }

  /**
   * 删除消息
   */
  const deleteMessage = async (messageId: number) => {
    try {
      const response = await messageService.deleteMessage(messageId)
      if (response.success) {
        await updateUnreadCount()
      }
    } catch (error) {
      console.error('删除消息失败:', error)
      throw error
    }
  }

  const setFilters = (newFilters: Partial<typeof filters.value>) => {
    filters.value = { ...filters.value, ...newFilters }
    currentPage.value = 1
  }

  const setPage = (page: number) => {
    currentPage.value = page
    fetchMessages()
  }

  const setPageSize = (size: number) => {
    pageSize.value = size
    currentPage.value = 1
    fetchMessages()
  }

  const showSuccess = (content: string) => {
    message.success(content)
  }

  const showError = (content: string) => {
    message.error(content)
  }

  return {
    messages,
    total,
    currentPage,
    pageSize,
    loading,
    unreadCount,
    filters,
    fetchMessages,
    updateUnreadCount,
    markAsRead,
    markMultipleAsRead,
    deleteMessage,
    setFilters,
    setPage,
    setPageSize,
    showSuccess,
    showError
  }
})
